import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import api from '../../services/api';
import { groupAPI } from '../../services/api';

const emptyViewForm = {
  name: '',
  description: '',
  shared: false
};

const ORIGIN_LABELS = {
  'virtual-medic': 'Virtual Medic',
  tenant: 'Tenant',
  external: 'External'
};

const AUTHOR_LABELS = {
  'virtual-medic-instructor': 'Virtual Medic instructor',
  'tenant-author': 'Tenant author',
  'external-contributor': 'External contributor',
  system: 'System'
};

const PerformanceFilterPanel = ({
  filters,
  onFiltersChange,
  savedViews = [],
  onCreateView,
  onSelectView,
  selectedViewId,
  onClearView
}) => {
  const [cases, setCases] = useState([]);
  const [groups, setGroups] = useState([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewForm, setViewForm] = useState(emptyViewForm);

  const loadFiltersData = useCallback(async () => {
    try {
      const [caseResponse, groupResponse] = await Promise.all([
        api.get('/cases?limit=1000'),
        groupAPI.list()
      ]);

      setCases(caseResponse.data?.cases || []);
      setGroups(groupResponse.data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to load filter metadata', error);
    }
  }, []);

  useEffect(() => {
    loadFiltersData();
  }, [loadFiltersData]);

  const caseOptions = useMemo(() => cases.map((item) => ({
    id: item._id || item.id,
    label: item.title
  })), [cases]);

  const groupOptions = useMemo(() => groups.map((item) => ({
    id: item._id || item.id,
    label: item.name
  })), [groups]);

  const provenanceOptions = useMemo(() => {
    const origins = new Set();
    const authors = new Set();

    cases.forEach((item) => {
      const provenance = item?.provenance || {};
      if (provenance.originType) {
        origins.add(provenance.originType);
      }
      if (provenance.authorType) {
        authors.add(provenance.authorType);
      }
    });

    return {
      origins: Array.from(origins).sort(),
      authors: Array.from(authors).sort()
    };
  }, [cases]);

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const applyDateRange = (key, value) => {
    handleFilterChange(key, value);
  };

  const openViewDialog = () => {
    setViewForm(emptyViewForm);
    setViewDialogOpen(true);
  };

  const closeViewDialog = () => {
    setViewDialogOpen(false);
  };

  const submitView = () => {
    if (!viewForm.name.trim()) {
      return;
    }
    onCreateView({
      ...viewForm,
      filters
    });
    closeViewDialog();
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          type="date"
          label="Start date"
          value={filters.startDate || ''}
          onChange={(event) => applyDateRange('startDate', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label="End date"
          value={filters.endDate || ''}
          onChange={(event) => applyDateRange('endDate', event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <FormControl sx={{ minWidth: 160 }}>
          <InputLabel id="granularity-label">Granularity</InputLabel>
          <Select
            labelId="granularity-label"
            value={filters.granularity || 'day'}
            label="Granularity"
            onChange={(event) => handleFilterChange('granularity', event.target.value)}
          >
            <MenuItem value="day">Daily</MenuItem>
            <MenuItem value="week">Weekly</MenuItem>
            <MenuItem value="month">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="case-filter-label">Cases</InputLabel>
          <Select
            labelId="case-filter-label"
            multiple
            value={filters.caseIds || []}
            label="Cases"
            onChange={(event) => handleFilterChange('caseIds', event.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = caseOptions.find(item => item.id === value);
                  return <Chip key={value} label={option ? option.label : value} size="small" />;
                })}
              </Box>
            )}
          >
            {caseOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="group-filter-label">Cohorts</InputLabel>
          <Select
            labelId="group-filter-label"
            multiple
            value={filters.groupIds || []}
            label="Cohorts"
            onChange={(event) => handleFilterChange('groupIds', event.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = groupOptions.find(item => item.id === value);
                  return <Chip key={value} label={option ? option.label : value} size="small" />;
                })}
              </Box>
            )}
          >
            {groupOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="origin-filter-label">Content origin</InputLabel>
          <Select
            labelId="origin-filter-label"
            multiple
            value={filters.originTypes || []}
            label="Content origin"
            onChange={(event) => handleFilterChange('originTypes', event.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={ORIGIN_LABELS[value] || value} size="small" />
                ))}
              </Box>
            )}
          >
            {provenanceOptions.origins.map((origin) => (
              <MenuItem key={origin} value={origin}>{ORIGIN_LABELS[origin] || origin}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel id="author-filter-label">Author type</InputLabel>
          <Select
            labelId="author-filter-label"
            multiple
            value={filters.authorTypes || []}
            label="Author type"
            onChange={(event) => handleFilterChange('authorTypes', event.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={AUTHOR_LABELS[value] || value} size="small" />
                ))}
              </Box>
            )}
          >
            {provenanceOptions.authors.map((author) => (
              <MenuItem key={author} value={author}>{AUTHOR_LABELS[author] || author}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel id="saved-view-label">Saved views</InputLabel>
          <Select
            labelId="saved-view-label"
            value={selectedViewId || ''}
            label="Saved views"
            onChange={(event) => onSelectView(event.target.value)}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {savedViews.map((view) => (
              <MenuItem key={view.id} value={view.id}>{view.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={openViewDialog}>
            Save current filters
          </Button>
          {selectedViewId && (
            <Button variant="text" onClick={onClearView}>
              Clear view
            </Button>
          )}
        </Stack>
      </Stack>

      <Dialog open={viewDialogOpen} onClose={closeViewDialog} fullWidth maxWidth="sm">
        <DialogTitle>Save filter view</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="View name"
              value={viewForm.name}
              onChange={(event) => setViewForm((prev) => ({ ...prev, name: event.target.value }))}
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={viewForm.description}
              onChange={(event) => setViewForm((prev) => ({ ...prev, description: event.target.value }))}
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewDialog}>Cancel</Button>
          <Button onClick={submitView} variant="contained">Save view</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.secondary">
        Saved views are scoped to your instructor account. Shared views are available for other instructors within your tenant via the backend API.
      </Typography>
    </Stack>
  );
};

export default PerformanceFilterPanel;
